import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Search, Calendar, Sparkles } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

const STORAGE_KEY = 'bookvia-how-it-works-seen';

export function HowItWorksModal() {
  const { language } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
      // Small delay so it doesn't fight the first paint / other overlays
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    } catch {
      // localStorage may be unavailable in private mode
    }
  }, []);

  const handleClose = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setOpen(false);
  };

  const steps = [
    {
      icon: Search,
      title: language === 'es' ? 'Busca' : 'Search',
      desc: language === 'es' ? 'Servicios cerca de ti.' : 'Services near you.',
    },
    {
      icon: Calendar,
      title: language === 'es' ? 'Reserva' : 'Book',
      desc: language === 'es' ? 'Fecha y hora al instante.' : 'Pick date and time.',
    },
    {
      icon: Sparkles,
      title: language === 'es' ? 'Disfruta' : 'Enjoy',
      desc: language === 'es' ? 'Sin filas ni esperas.' : 'No lines, no waits.',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="w-[92vw] max-w-sm p-0 overflow-hidden border-0 rounded-2xl max-h-[90vh]"
        data-testid="how-it-works-modal"
      >
        {/* Top color bar */}
        <div className="h-1 w-full bg-gradient-to-r from-[#F05D5E] via-[#fcf7ba] to-[#F05D5E]" />

        <div className="px-5 pt-4 pb-4">
          <DialogHeader className="text-center mb-3">
            <DialogTitle className="font-heading text-lg sm:text-xl text-slate-900 leading-tight">
              {language === 'es' ? '¿Cómo funciona Bookvia?' : 'How does Bookvia work?'}
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {language === 'es'
                ? 'Reservar nunca fue tan simple.'
                : 'Booking has never been this simple.'}
            </p>
          </DialogHeader>

          {/* Steps - compact */}
          <div className="space-y-2">
            {steps.map((step, idx) => (
              <div
                key={step.title}
                className="flex items-center gap-3 p-2 rounded-lg"
                data-testid={`how-it-works-step-${idx + 1}`}
              >
                <div className="relative shrink-0">
                  <div className="h-9 w-9 rounded-lg bg-[#F05D5E]/10 flex items-center justify-center">
                    <step.icon className="h-4 w-4 text-[#F05D5E]" />
                  </div>
                  <span className="absolute -top-1 -left-1 h-4 w-4 rounded-full bg-slate-900 text-white text-[9px] font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-sm text-slate-900 leading-none">
                    {step.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button
            onClick={handleClose}
            className="w-full mt-4 btn-coral h-10 text-sm font-semibold"
            data-testid="how-it-works-close-btn"
          >
            {language === 'es' ? '¡Entendido!' : "Got it!"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
